import React from 'react';
import { useRouteLoaderData } from 'react-router-dom';

import {
  type AgencyUserFieldsFragment,
  type LoggedInAgencyUserFieldsFragment,
  SubscriptionEntitlement
} from '~/api/graphql';

type AppLoaderData = {
  agencyUser?: LoggedInAgencyUserFieldsFragment;
  agencyUsers?: AgencyUserFieldsFragment[];
};

/**
 * Resolves the agency-user roster and Pro-tier entitlement once the `app`
 * route's deferred loader settles. Returns:
 *
 *  - `userNameById`: stable lookup used by the columns + filters (custom
 *    forms display the form's creator name, which isn't on the fragment).
 *  - `hasProEntitlement`: whether the agency may use the Custom Forms
 *    feature; the page gates the upload action behind a Pro upsell.
 *
 * Until the loader resolves the entitlement defaults to `true` so the page
 * doesn't flash an upsell to entitled users.
 */
export function useCustomFormsContext() {
  const appLoader = useRouteLoaderData('app') as { data?: Promise<AppLoaderData> } | undefined;

  const [agencyUsers, setAgencyUsers] = React.useState<AgencyUserFieldsFragment[]>([]);
  const [hasProEntitlement, setHasProEntitlement] = React.useState(true);

  React.useEffect(() => {
    // Cancel-guard: when the route revalidates we may get a newer promise
    // before the older one resolves. Without this, an older payload could
    // overwrite fresher state, and unmounting mid-flight would setState on
    // an unmounted component.
    let cancelled = false;
    appLoader?.data?.then((d) => {
      if (cancelled) return;
      if (d.agencyUsers) setAgencyUsers(d.agencyUsers);
      const hasPro = d.agencyUser?.agency?.entitlements?.some(
        (e) => e === SubscriptionEntitlement.CopilotPdfEditor
      );
      setHasProEntitlement(hasPro !== false);
    });
    return () => {
      cancelled = true;
    };
  }, [appLoader]);

  const userNameById = React.useCallback(
    (id: string) => agencyUsers.find((u) => u.id === id)?.name ?? id,
    [agencyUsers]
  );

  return { agencyUsers, userNameById, hasProEntitlement };
}
